/**
 * Utility functions for salary conversion
 */
import { EUR_PER_UNIT } from '../../../../../utils/currency/eur-exchange-rates';

// currency code -> EUR per unit. Shared ECB-sourced table, see utils/currency/eur-exchange-rates.ts.
const CURRENCY_RATES: Readonly<Record<string, number>> = EUR_PER_UNIT;

export interface ConvertedSalary {
  min: number;
  max: number;
  currency: string;
  period: 'monthly';
}

export interface OriginalSalary {
  min: number;
  max: number;
  currency: string;
  period: 'annual' | 'monthly' | 'daily' | 'hourly';
}

/**
 * Convert salary to EUR per month
 */
export function convertSalaryToMonthlyEUR(
  min: number | undefined,
  max: number | undefined,
  currency: string | undefined,
  period: 'annual' | 'monthly' | 'daily' | 'hourly' | undefined
): ConvertedSalary | null {
  if (min === undefined && max === undefined) {
    return null;
  }

  const originalCurrency = currency || 'EUR';
  const originalPeriod = period || 'monthly';
  
  // Check if currency is supported
  const rate = CURRENCY_RATES[originalCurrency];
  if (rate === undefined) {
    // Currency not supported, return null
    return null;
  }

  // Convert to monthly
  let monthlyMin: number | undefined;
  let monthlyMax: number | undefined;

  if (originalPeriod === 'annual') {
    monthlyMin = min !== undefined ? min / 12 : undefined;
    monthlyMax = max !== undefined ? max / 12 : undefined;
  } else if (originalPeriod === 'hourly') {
    monthlyMin = min !== undefined ? min * 160 : undefined; // 160 hours per month
    monthlyMax = max !== undefined ? max * 160 : undefined;
  } else if (originalPeriod === 'daily') {
    monthlyMin = min !== undefined ? min * 22 : undefined; // ~22 workdays/month
    monthlyMax = max !== undefined ? max * 22 : undefined;
  } else {
    // Already monthly or unknown (default to monthly)
    monthlyMin = min;
    monthlyMax = max;
  }

  // Convert currency to EUR
  const eurMin = monthlyMin !== undefined ? Math.round(monthlyMin * rate) : undefined;
  const eurMax = monthlyMax !== undefined ? Math.round(monthlyMax * rate) : undefined;

  if (eurMin === undefined && eurMax === undefined) {
    return null;
  }

  return {
    min: eurMin ?? eurMax ?? 0,
    max: eurMax ?? eurMin ?? 0,
    currency: 'EUR',
    period: 'monthly',
  };
}

/**
 * Check if original salary differs from EUR/month
 */
export function hasOriginalSalary(
  currency: string | undefined,
  period: 'annual' | 'monthly' | 'daily' | 'hourly' | undefined
): boolean {
  const originalCurrency = currency || 'EUR';
  const originalPeriod = period || 'monthly';
  
  return originalCurrency !== 'EUR' || originalPeriod !== 'monthly';
}

/**
 * Get currency symbol
 */
export function getCurrencySymbol(currency: string | undefined): string {
  const currencyMap: Record<string, string> = {
    EUR: '€',
    USD: '$',
    GBP: '£',
    JPY: '¥',
    CNY: '¥',
    KRW: '₩',
    INR: '₹',
    BRL: 'R$',
    MXN: '$',
    CAD: 'C$',
    AUD: 'A$',
    NZD: 'NZ$',
    CHF: 'CHF',
    SEK: 'kr',
    NOK: 'kr',
    DKK: 'kr',
    PLN: 'zł',
    HUF: 'Ft',
    CZK: 'Kč',
    TRY: '₺',
    ZAR: 'R',
    SGD: 'S$',
    HKD: 'HK$',
    AED: 'AED',
    SAR: 'SAR',
  };
  
  return currencyMap[currency || 'EUR'] || (currency || 'EUR');
}
